// App
import isNil from 'lodash/isNil';
import numeral from 'numeral';

// Constellation
import { Box, Button, ModalTrigger, Text } from '@zillow/constellation-10';

// Flux
import { useAppSelector } from '../flux/configureStore';

// Misc / utils
import { parser as comparablesParser } from '../utils/comparablesUtils';
import { pluralize } from '../utils/stringUtils';
import { compact, trimDecimal } from '../utils/numberUtils';
import HomeMarker from '../images/icons/homeMarker.inline.svg';
import {
  cardAddressStyles,
  cardContainerRecipe,
  myPropertyBannerStyles,
  myPropertyFactsContainerStyles,
  myPropertyHeaderStyles,
} from './styles';

// Types
import type Comparables from '../models/Comparables';

type ZestimateType = { rentZestimate: number };
type MyPropertyCardPropTypes = {
  listing: Comparables & Partial<ZestimateType>;
  isHovered?: boolean;
  lockHover?: boolean;
};

const MAX_CHARS_PER_LINE = 21;

const MyPropertyCardComponent = ({ listing, isHovered, lockHover }: MyPropertyCardPropTypes) => {
  const longestAddress = useAppSelector((state) => comparablesParser.getLongestAddress(state.comparables.items));
  const priceFilter = useAppSelector((state) => state.filter.price);
  // prevent js error when setting custom price for addresses without a zestimate
  const myPropertyDisplayPrice =
    priceFilter || listing.rentZestimate ? compact(priceFilter || listing.rentZestimate) : null;
  const myPropertyPriceSqft =
    '$' + trimDecimal(numeral(myPropertyDisplayPrice).value() / numeral(listing.sqft).value(), 2);

  const listingPriceSqft = isNil(listing.pricePerSqft) ? '—' : `${myPropertyPriceSqft} per sq ft`;

  const listingBeds = isNil(listing.beds) ? '—' : comparablesParser.beds(listing.beds);
  const listingBaths = isNil(listing.baths) ? '—' : pluralize('bath', listing.baths, true);
  const listingSqft = isNil(listing.sqft) ? '—' : `${listing.sqft} sq ft`;

  return (
    <Box css={cardContainerRecipe.raw({ isHovered: !!(isHovered || lockHover) })}>
      <Box css={myPropertyHeaderStyles}>
        <Text textStyle="body-sm-bold" css={myPropertyBannerStyles}>
          My property
        </Text>
        <Box css={{ height: '150px', textAlign: 'center', marginTop: '400' }}>
          <HomeMarker height="80" width="100" style={{ background: 'transparent' }} />
        </Box>
        <Text textStyle="body-sm-bold">{`$${myPropertyDisplayPrice}/mo`}</Text>
        <Text textStyle="body-sm">{listingPriceSqft}</Text>
      </Box>
      <Box
        css={{
          ...cardAddressStyles,
          height: longestAddress.length > MAX_CHARS_PER_LINE ? '143px' : '105px',
        }}
      >
        {longestAddress.length > MAX_CHARS_PER_LINE ? (
          <Text textStyle="body-sm" css={{ marginBottom: '200', marginTop: '27px' }}>
            {listing.street}
          </Text>
        ) : (
          <Text textStyle="body-sm" css={{ marginBlock: '200' }}>
            {listing.street}
          </Text>
        )}
        <ModalTrigger multiModalTriggerId="change-price-modal">
          <Button size="sm" tone="brand" emphasis="filled" css={{ marginBottom: '200' }}>
            Adjust price
          </Button>
        </ModalTrigger>
      </Box>
      <Box css={myPropertyFactsContainerStyles}>
        <Text textStyle="body-sm-bold" css={{ marginBlock: '200' }}>
          {listingBeds}
        </Text>
        <Text textStyle="body-sm-bold" css={{ marginBottom: '200' }}>
          {listingBaths}
        </Text>
        <Text textStyle="body-sm-bold" css={{ marginBottom: '200' }}>
          {listingSqft}
        </Text>
      </Box>
    </Box>
  );
};

const MyPropertyCard = (props: MyPropertyCardPropTypes) => {
  const { listing } = props;
  if (!listing) {
    return null;
  }
  return <MyPropertyCardComponent {...props} />;
};

export default MyPropertyCard;
